// STEP LIVE-CONSOLE-REALTIME-SUMMARY-1 · Compact LIVE 실시간 현황 bar.
//
// 지시 §0 REUSE FIRST · 지시 §2 Compact · 지시 §3 매출 정의 · 지시 §6 새 탭.
//
// Data sources (REUSE · 신규 집계 엔진 추가 0):
//   1) GET /seller/orders/summary  (STEP 에서 amount_by_status 필드 추가 ·
//      기존 by_status counts 응답 하위에 확장).
//        · 매출기준(입금대기) = amount_by_status.payment_pending · count = by_status.payment_pending
//        · 매출입금완료(입금) = sum(amount_by_status[paid|preparing|shipping|delivered|confirmed])
//          · count 도 동일 집계 (취소/환불요청/환불완료 명시적 제외 · 지시 §3)
//   2) GET /seller/broadcasts/{sid}/comment-candidates?tab=all&limit=500
//      · 댓글감지 = items.length
//      · 주문후보 = sum(pending + needs_option + needs_review)
//      · 확정완료 = sum(status==='confirmed')
//
// Click 동작 (지시 §6 · 다른 관리 페이지로 이동 = 새 탭):
//   · 매출 영역 · 주문 상세 는 target="_blank" rel="noopener noreferrer".
//   · 댓글 count 는 Console 내부 anchor scroll (현재 route 유지 · 새 탭 X).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, ExternalLink } from 'lucide-react';
import api from '@/lib/api';


// 매출 완료 집계에 포함하는 상태 (지시 §3 · 취소/환불/실패 명시적 제외).
const PAID_FLOW_STATUSES = Object.freeze([
  'paid', 'preparing', 'shipping', 'delivered', 'confirmed',
]);


const _fmtWon = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;


const _sumByStatuses = (map, keys) => {
  if (!map || typeof map !== 'object') return 0;
  let total = 0;
  for (const k of keys) total += Number(map[k] || 0);
  return total;
};


// 새 탭 안전 open helper (noopener/noreferrer · 지시 §6).
const _openManagementPage = (path) => {
  try {
    const w = window.open(path, '_blank', 'noopener,noreferrer');
    if (w && typeof w.opener !== 'undefined') { try { w.opener = null; } catch {} }
  } catch { /* popup blocked · 사용자가 다시 클릭하면 됨 */ }
};


export default function CompactLiveSummary({ scheduleId }) {
  const [busy, setBusy] = useState(false);
  const [ordersSummary, setOrdersSummary] = useState({
    by_status: {}, amount_by_status: {},
  });
  const [candCounts, setCandCounts] = useState({
    detected: 0, candidates: 0, confirmed: 0,
  });

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [rSum, rCand] = await Promise.all([
        api.get('/seller/orders/summary').catch(() => ({ data: {} })),
        scheduleId
          ? api.get(`/seller/broadcasts/${scheduleId}/comment-candidates`, {
              params: { tab: 'all', limit: 500 },
            }).catch(() => ({ data: {} }))
          : Promise.resolve({ data: {} }),
      ]);
      setOrdersSummary({
        by_status: rSum.data?.by_status || {},
        amount_by_status: rSum.data?.amount_by_status || {},
      });
      const items = Array.isArray(rCand.data?.items) ? rCand.data.items : [];
      let candidates = 0;
      let confirmed = 0;
      for (const it of items) {
        const s = it.status;
        if (s === 'pending' || s === 'needs_option' || s === 'needs_review') {
          candidates += 1;
        } else if (s === 'confirmed') {
          confirmed += 1;
        }
      }
      setCandCounts({
        detected: items.length,
        candidates,
        confirmed,
      });
    } finally {
      setBusy(false);
    }
  }, [scheduleId]);

  useEffect(() => { load(); }, [load]);
  // 20s poll · 방송 중 숫자 실시간 유지 (기존 Phase55 폴링 주기와 동일).
  useEffect(() => {
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  const pendingAmount = Number(ordersSummary.amount_by_status?.payment_pending || 0);
  const pendingCount  = Number(ordersSummary.by_status?.payment_pending || 0);
  const paidAmount    = useMemo(
    () => _sumByStatuses(ordersSummary.amount_by_status, PAID_FLOW_STATUSES),
    [ordersSummary.amount_by_status]);
  const paidCount     = useMemo(
    () => _sumByStatuses(ordersSummary.by_status, PAID_FLOW_STATUSES),
    [ordersSummary.by_status]);

  // 상세 관리 페이지 route — 기존 SellerDashboardV2 section/stage navigation REUSE.
  const PENDING_URL = '/seller/dashboard?section=orders&stage=order_confirm';
  const PAID_URL    = '/seller/dashboard?section=orders&stage=paid';
  const SCROLL_ID   = 'phase7-comment-panel';

  const scrollToComments = () => {
    const el = document.getElementById(SCROLL_ID);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="rounded-xl border border-[#FF3B30]/30 bg-[#FF3B30]/[0.03] px-3 py-2"
         data-testid="compact-live-summary">
      <div className="flex items-center gap-2 mb-1.5">
        <p className="text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1 text-[#FF3B30]">
          <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#FF3B30] animate-pulse" />
          LIVE 실시간 현황
        </p>
        <button onClick={load} disabled={busy}
                className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold border border-black/10 dark:border-white/10 hover:border-[#FF3B30] disabled:opacity-50"
                data-testid="compact-live-summary-refresh">
          <RefreshCw size={10} className={busy ? 'animate-spin' : ''} /> 새로고침
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs">
        {/* 매출기준(입금대기) — amount + count · 새 탭 open */}
        <button type="button"
                onClick={() => _openManagementPage(PENDING_URL)}
                className="inline-flex items-baseline gap-1.5 hover:text-[#FF3B30] group"
                data-testid="compact-sum-pending"
                title="새 탭에서 결제대기 주문 상세로 이동">
          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 group-hover:text-[#FF3B30]">
            매출기준(입금대기)
          </span>
          <span className="font-black tabular-nums"
                data-testid="compact-sum-pending-amount">{_fmtWon(pendingAmount)}</span>
          <span className="text-gray-500" data-testid="compact-sum-pending-count">
            · {pendingCount}건
          </span>
          <ExternalLink size={10} className="text-gray-400 group-hover:text-[#FF3B30]" />
        </button>

        <span className="hidden md:inline-block w-px h-3 bg-black/10 dark:bg-white/15" />

        {/* 매출입금완료(입금) — amount + count · 새 탭 open */}
        <button type="button"
                onClick={() => _openManagementPage(PAID_URL)}
                className="inline-flex items-baseline gap-1.5 hover:text-emerald-700 group"
                data-testid="compact-sum-paid"
                title="새 탭에서 결제완료 이후 주문 상세로 이동">
          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500 group-hover:text-emerald-700">
            매출입금완료(입금)
          </span>
          <span className="font-black tabular-nums text-emerald-700 dark:text-emerald-300"
                data-testid="compact-sum-paid-amount">{_fmtWon(paidAmount)}</span>
          <span className="text-gray-500" data-testid="compact-sum-paid-count">
            · {paidCount}건
          </span>
          <ExternalLink size={10} className="text-gray-400 group-hover:text-emerald-700" />
        </button>

        <span className="hidden md:inline-block w-px h-3 bg-black/10 dark:bg-white/15 ml-auto" />

        {/* 댓글/주문 count — Console 내부 anchor scroll (route 유지 · 새 탭 X) */}
        <button type="button" onClick={scrollToComments}
                className="inline-flex items-baseline gap-1 hover:text-[#FF3B30]"
                data-testid="compact-cnt-detected">
          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">댓글감지</span>
          <span className="font-black tabular-nums">{candCounts.detected}</span>
        </button>
        <button type="button" onClick={scrollToComments}
                className="inline-flex items-baseline gap-1 hover:text-[#FF3B30]"
                data-testid="compact-cnt-candidates">
          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">주문후보</span>
          <span className="font-black tabular-nums text-[#FF3B30]">{candCounts.candidates}</span>
        </button>
        <button type="button" onClick={scrollToComments}
                className="inline-flex items-baseline gap-1 hover:text-emerald-700"
                data-testid="compact-cnt-confirmed">
          <span className="text-[10px] font-bold uppercase tracking-widest text-gray-500">확정완료</span>
          <span className="font-black tabular-nums text-emerald-700 dark:text-emerald-300">{candCounts.confirmed}</span>
        </button>
      </div>
    </div>
  );
}

// 테스트/다른 모듈에서 재사용할 수 있도록 export (지시 §3 상태 집합 anchor).
export { PAID_FLOW_STATUSES };
